/*
 * Lista de pagos.
 *
 * Lista de pagos que muestra los detalles relacionados con estos. La lista tiene apartados
 * para mostrar todos los registros, pagos que han sido realizados con efectivo, devoluciones
 * y métricas.
 *
 * Created on Fri Aug 04 2023 by José Luis Chávez Ayala.
 *
 * Copyright (c) 2023 Turn My App.
 */

<template>
  <div>

    <!-- #region::Skeleton -->
    <template v-if="isLoadingPaymentsData">
      <BasicSkeleton height="150px" />
      <BasicSkeleton height="450px" />
    </template>
    <!-- #endregion::Skeleton -->

    <template v-else>

      <b-tabs v-model="selectedIndex">

        <!-- #region::All payments -->
        <b-tab
          v-if="$ability.can('read all', 'Payments')"
          title="Todos"
          lazy
          @click="onSelectedTab('all')"
        >
          <OrdersPaymentsList
            title="all"
            :download-file-base-link="'/reports/payments/csv'"
          />
        </b-tab>
        <!-- #endregion::All payments -->

        <!-- #region::Cash payments -->
        <b-tab
          v-if="$ability.can('read cash', 'Payments')"
          title="Efectivo"
          lazy
          @click="onSelectedTab('cash')"
        >
          <OrdersPaymentsList
            title="cash"
            :show-only-cash-items="true"
            :download-file-base-link="'/reports/payments/csv'"
          />
        </b-tab>
        <!-- #region::Cash payments -->

        <b-tab
          v-if="$ability.can('read devolutions', 'Payments')"
          title="Devoluciones"
          lazy
          @click="onSelectedTab('refunds')"
        >
          <RefundsPaymentsList
            title="refunds"
            :download-file-base-link="'/reports/devolutions/csv'"
          />
        </b-tab>
        <b-tab
          title="Dashboard"
          lazy
        >
          <p>
            <DashboardPaymentsLists
              @on:filter-by-location="filterPaymentsByLocation"
              @on:filter-by-method="filterPaymentsByMethod"
              @on:filter-by-cash-status="filterPaymentsByCashStatus"
            />
          </p>
        </b-tab>
        <b-tab
          title="Transacciones"
          lazy
        >
          <p>
            <TransactionsPaymentsLists />
          </p>
        </b-tab>
      </b-tabs>

    </template>

  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import {
  BTabs, BTab,
} from 'bootstrap-vue'

import BasicSkeleton from '@/components/cards/BasicSkeleton.vue'
import OrdersPaymentsList from '@/modules/reports/payments/components/OrdersPaymentsList.vue'
import RefundsPaymentsList from '@/modules/reports/payments/components/RefundsPaymentsList.vue'
import DashboardPaymentsLists from '@/modules/reports/payments/components/DashboardPaymentsLists.vue'
import TransactionsPaymentsLists from '@/modules/reports/payments/components/TransactionsPaymentsLists.vue'

export default {
  components: {
    BTab,
    BTabs,
    BasicSkeleton,
    OrdersPaymentsList,
    RefundsPaymentsList,
    DashboardPaymentsLists,
    TransactionsPaymentsLists,
  },
  data() {
    return {
      selectedIndex: 0,
      isLoadingPaymentsData: false,
    }
  },
  computed: {
    ...mapGetters({
      getTabs: 'payments/getTabs',
      getListsParams: 'payments/getListsParams',
      /**
       * Filtros globales
       */
      getFilteredCreator: 'filters/getFilteredCreator',
      getFilteredPerPage: 'filters/getFilteredPerPage',
      getFilteredLocation: 'filters/getFilteredLocation',
      getFilteredSearchTerm: 'filters/getFilteredSearchTerm',
      getFilteredOrderStatus: 'filters/getFilteredOrderStatus',
      getFilteredQuoteStatus: 'filters/getFilteredQuoteStatus',
      getFilteredCurrentPage: 'filters/getFilteredCurrentPage',
      getFilteredPaymentStatus: 'filters/getFilteredPaymentStatus',
      getFilteredPaymentMethod: 'filters/getFilteredPaymentMethod',
      getFilteredPaymentCurrency: 'filters/getFilteredPaymentCurrency',
      getFilteredVoucherDateRange: 'filters/getFilteredVoucherDateRange',
      getFilteredRegisterDateRange: 'filters/getFilteredRegisterDateRange',
      getFilteredDeliveryDateRange: 'filters/getFilteredDeliveryDateRange',
      getFilteredOrderPaymentStatus: 'filters/getFilteredOrderPaymentStatus',
      getFilteredCashDeliveryStatus: 'filters/getFilteredCashDeliveryStatus',
    }),
    /**
     * Lista de pestañas
     */
    tabs: {
      get() { return this.getTabs },
    },
    /**
     * Lista de parámetros de listas
     */
    listsParams: {
      get() { return this.getListsParams },
    },
    /**
     * Indica el creador seleccionado (paginado global)
     */
    filteredCreator: {
      get() { return this.getFilteredCreator },
      set(value) { this.setFilteredCreator(value) },
    },
    /**
     * Indica el número de registros por página (paginado global)
     */
    filteredPerPage: {
      get() { return this.getFilteredPerPage },
      set(value) { this.setFilteredPerPage(value) },
    },
    /**
     * Indica la ubicación seleccionada (paginado global)
     */
    filteredLocation: {
      get() { return this.getFilteredLocation },
      set(value) { this.setFilteredLocation(value) },
    },
    /**
     * Indica el término de búsqueda (paginado global)
     */
    filteredSearchTerm: {
      get() { return this.getFilteredSearchTerm },
      set(value) { this.setFilteredSearchTerm(value) },
    },
    /**
     * Indica el estado de la orden seleccionado (paginado global)
     */
    filteredOrderStatus: {
      get() { return this.getFilteredOrderStatus },
      set(value) { this.setFilteredOrderStatus(value) },
    },
    /**
     * Indica el estado de la cotización seleccionado (paginado global)
     */
    filteredQuoteStatus: {
      get() { return this.getFilteredQuoteStatus },
      set(value) { this.setFilteredQuoteStatus(value) },
    },
    /**
     * Indica la página actual (paginado global)
     */
    filteredCurrentPage: {
      get() { return this.getFilteredCurrentPage },
      set(value) { this.setFilteredCurrentPage(value) },
    },
    /**
     * Indica el estado de pago seleccionado (paginado global)
     */
    filteredPaymentStatus: {
      get() { return this.getFilteredPaymentStatus },
      set(value) { this.setFilteredPaymentStatus(value) },
    },
    /**
     * Indica el método de pago seleccionado (paginado global)
     */
    filteredPaymentMethod: {
      get() { return this.getFilteredPaymentMethod },
      set(value) { this.setFilteredPaymentMethod(value) },
    },
    /**
     * Indica la moneda seleccionada (paginado global)
     */
    filteredPaymentCurrency: {
      get() { return this.getFilteredPaymentCurrency },
      set(value) { this.setFilteredPaymentCurrency(value) },
    },
    /**
     * Indica el rango de fechas del comprobante (paginado global)
     */
    filteredVoucherDateRange: {
      get() { return this.getFilteredVoucherDateRange },
      set(value) { this.setFilteredVoucherDateRange(value) },
    },
    /**
     * Indica el rango de fechas de registro (paginado global)
     */
    filteredRegisterDateRange: {
      get() { return this.getFilteredRegisterDateRange },
      set(value) { this.setFilteredRegisterDateRange(value) },
    },
    /**
     * Indica el rango de fechas de entrega (paginado global)
     */
    filteredDeliveryDateRange: {
      get() { return this.getFilteredDeliveryDateRange },
      set(value) { this.setFilteredDeliveryDateRange(value) },
    },
    /**
     * Indica el estado de pago de la orden seleccionado (paginado global)
     */
    filteredOrderPaymentStatus: {
      get() { return this.getFilteredOrderPaymentStatus },
      set(value) { this.setFilteredOrderPaymentStatus(value) },
    },
    /**
     * Indica el estado de entrega de la orden seleccionado (paginado global)
     */
    filteredCashDeliveryStatus: {
      get() { return this.getFilteredCashDeliveryStatus },
      set(value) { this.setFilteredCashDeliveryStatus(value) },
    },
  },

  /**
  * Hook de la creación de componente
  *
  * @summary Carga la información de los filtros de columnas
  */
  async created() {
    this.isLoadingPaymentsData = true
    /**
     * Carga la lista de métodos de pago para los filtros
     * en la pestaña de todos y efectivo
     */
    await this.loadQuotesPaymentMethods()
    await this.loadRefundsPaymentMethods()
    /**
     * Carga la información de los filtros de columnas para
     * mostrar y ocultar columnas en las listas
     */
    await this.loadOrdersStatus()
    await this.loadOrdersColumnsList()
    await this.loadRefundsColumnsList()
    await this.loadCashOrdersColumnsList()
    this.isLoadingPaymentsData = false
  },

  /**
  * Hook de la eliminación de componente
  *
  * @summary Limpia toda la informacion del módulo de reportes de proveedores
  */
  beforeDestroy() {
    this.clearReportsPaymentsInfo()
  },
  methods: {
    ...mapActions({
      saveListParams: 'payments/saveListParams',
      loadOrdersStatus: 'payments/loadOrdersStatus',
      loadOrdersColumnsList: 'payments/loadOrdersColumnsList',
      loadRefundsColumnsList: 'payments/loadRefundsColumnsList',
      clearReportsPaymentsInfo: 'payments/clearReportsPaymentsInfo',
      loadCashOrdersColumnsList: 'payments/loadCashOrdersColumnsList',
      /**
       * Métodos de pago para filtros
       */
      loadQuotesPaymentMethods: 'quotes/loadPaymentMethods',
      loadRefundsPaymentMethods: 'refunds/loadPaymentMethods',
      /**
       * Filtros globales
       */
      setFilteredCreator: 'filters/setFilteredCreator',
      setFilteredPerPage: 'filters/setFilteredPerPage',
      setFilteredLocation: 'filters/setFilteredLocation',
      setFilteredSearchTerm: 'filters/setFilteredSearchTerm',
      setFilteredOrderStatus: 'filters/setFilteredOrderStatus',
      setFilteredQuoteStatus: 'filters/setFilteredQuoteStatus',
      setFilteredCurrentPage: 'filters/setFilteredCurrentPage',
      setFilteredPaymentStatus: 'filters/setFilteredPaymentStatus',
      setFilteredPaymentMethod: 'filters/setFilteredPaymentMethod',
      setFilteredPaymentCurrency: 'filters/setFilteredPaymentCurrency',
      setFilteredVoucherDateRange: 'filters/setFilteredVoucherDateRange',
      setFilteredRegisterDateRange: 'filters/setFilteredRegisterDateRange',
      setFilteredDeliveryDateRange: 'filters/setFilteredDeliveryDateRange',
      setFilteredOrderPaymentStatus: 'filters/setFilteredOrderPaymentStatus',
      setFilteredCashDeliveryStatus: 'filters/setFilteredCashDeliveryStatus',
    }),

    /**
    * Evento de selección de pestaña
    * @summary Establece los filtros de búsqueda de la pestaña seleccionada. Si la pestaña ya ha sido
    * seleccionada anteriormente, se establecen los filtros de búsqueda de la pestaña seleccionada, de
    * lo contrario, se establecen los filtros de búsqueda por defecto
    * @param {String} title - Título de la pestaña seleccionada
    */
    onSelectedTab(title) {
      const params = this.listsParams.find(item => item.title === title)
      this.filteredCreator = params?.creatorTerm || ''
      this.filteredPerPage = params?.perPage || 10
      this.filteredLocation = params?.locationPaymentTerm || ''
      this.filteredSearchTerm = params?.searchTerm || ''
      this.filteredOrderStatus = params?.orderStatusTerm || ''
      this.filteredQuoteStatus = params?.quoteStatusTerm || ''
      this.filteredCurrentPage = params?.currentPage || 1
      this.filteredPaymentStatus = params?.statusConfirmationTerm || ''
      this.filteredPaymentMethod = params?.paymentMethodTerm || ''
      this.filteredPaymentCurrency = params?.paymentCurrencyTerm || ''

      this.filteredVoucherDateRange = params?.voucherDateStartTerm && params?.voucherDateEndTerm
        ? { from: params?.voucherDateStartTerm, to: params?.voucherDateEndTerm } : null

      this.filteredRegisterDateRange = params?.registerDateStartTerm && params?.registerDateEndTerm
        ? { from: params?.registerDateStartTerm, to: params?.registerDateEndTerm } : null

      this.filteredDeliveryDateRange = params?.deliveryDateStartTerm && params?.deliveryDateEndTerm
        ? { from: params?.deliveryDateStartTerm, to: params?.deliveryDateEndTerm } : null

      this.filteredOrderPaymentStatus = params?.paymentStatusTerm || ''
      this.filteredCashDeliveryStatus = params?.deliveryCashTerm || ''
    },

    /**
    * Filtro de pagos por ubicación
    * @summary Si existen parámetros de búsqueda en la pestaña seleccionada, se limpian. Establece
    * la pestaña seleccionada y el filtro de ubicación
    * @param {Object} filteredLocation - Objeto con el tipo y valor del filtro
    */
    filterPaymentsByLocation(filteredLocation) {
      this.clearListParams(filteredLocation.tab)
      this.onSelectedTab(filteredLocation.tab)

      // Se carga el filtro de ubicación y el rango de fechas de registro
      this.filteredLocation = filteredLocation.filter
      this.filteredRegisterDateRange = filteredLocation.dashboardDateRange

      if (filteredLocation.type === 'registeredPayments') {
        this.selectedIndex = 0
      } else if (filteredLocation.type === 'refundsPayments') {
        this.selectedIndex = 2
      }
    },

    /**
     * Filtro de pagos por método
     * @summary Filtra los pagos por método desde las listas de pagos registrados
     * y devoluciones de la pestaña de dashboard
     * @param {Object} filteredMethod - Objeto con el tipo y valor del filtro
     */
    filterPaymentsByMethod(filteredMethod) {
      this.clearListParams(filteredMethod.tab)
      this.onSelectedTab(filteredMethod.tab)

      // Se carga el filtro de método de pago y el rango de fechas de registro
      this.filteredPaymentMethod = filteredMethod.paymentMethod
      this.filteredRegisterDateRange = filteredMethod.dashboardDateRange
      this.selectedIndex = 0
    },

    /**
    * Filtro de pagos por estado de efectivo
    * @summary Filtra los pagos en efectivo por el estado de entrega desde
    * las listas de pagos en el dashboard
    * @param {Object} filteredCashStatus - Objeto con el tipo y valor del filtro
    */
    filterPaymentsByCashStatus(filteredCashStatus) {
      this.clearListParams(filteredCashStatus.tab)
      this.onSelectedTab(filteredCashStatus.tab)

      // Se carga el filtro moneda, estado de entrega y el rango de fechas de registro
      this.filteredPaymentMethod = filteredCashStatus.type
      this.filteredCashDeliveryStatus = filteredCashStatus.status
      this.filteredRegisterDateRange = filteredCashStatus.dashboardDateRange

      this.selectedIndex = 1
    },

    /**
    * Reseteo de estado de filtros
    * @summary Resetea los filtros de búsqueda de las listas de pagos cando se
    * selecciona una opciones de las tablas en la pestña de dashboard
    * @param {String} title - Título de la pestaña seleccionada
    */
    clearListParams(title) {
      const params = this.listsParams.find(item => item.title === title)

      if (params) {
        this.$set(params, 'creatorTerm', '')
        this.$set(params, 'perPage', 10)
        this.$set(params, 'locationPaymentTerm', '')
        this.$set(params, 'searchTerm', '')
        this.$set(params, 'currentPage', 1)
        this.$set(params, 'statusConfirmationTerm', '')
        this.$set(params, 'paymentMethodTerm', '')
        this.$set(params, 'paymentCurrencyTerm', '')
        this.$set(params, 'voucherDateStartTerm', undefined)
        this.$set(params, 'voucherDateEndTerm', undefined)
        this.$set(params, 'registerDateStartTerm', undefined)
        this.$set(params, 'registerDateEndTerm', undefined)
        this.$set(params, 'deliveryDateStartTerm', undefined)
        this.$set(params, 'deliveryDateEndTerm', undefined)
        this.$set(params, 'paymentStatusTerm', '')
        this.$set(params, 'deliveryCashTerm', '')
        this.$set(params, 'orderStatusTerm', '')
        this.$set(params, 'quoteStatusTerm', '')

        this.saveListParams(params)
      }
    },
  },
}
</script>
